// angular imports
import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

// 3rd party imports
import { Subscription } from 'rxjs';

// cc-common imports
import { Utils } from 'cc-common';

// base imports
import { WorkOrderBaseComponent } from '../../_base/work-order-base-component';

// component imports
import { OpenWorkOrdersComponent } from '../open-work-orders/open-work-orders.component';
import { ClosedWorkOrdersComponent } from '../closed-work-orders/closed-work-orders.component';
import { IInteractiveDashboardComponent } from '../../../dashboard-module/_interfaces/interactive-dashboard-component';

// service imports
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';

// model imports
import { WorkOrderRawDto } from '../../_models/work-order-raw-dto';

// constants imports
import { StringFormatConstants } from '../../../_constants/string-format-constants';

interface TabItem {
    name: string;
    text: string;
}

@Component({
    selector: 'app-work-order',
    templateUrl: './work-order.component.html',
    styleUrls: ['./work-order.component.scss']
})
export class WorkOrderComponent
    extends WorkOrderBaseComponent
    implements OnInit, OnDestroy, IInteractiveDashboardComponent
{
    @ViewChild('openWorkOrders', { static: false }) openWorkOrders: OpenWorkOrdersComponent;
    @ViewChild('closedWorkedOrders', { static: false }) closedWorkedOrders: ClosedWorkOrdersComponent;

    @Input() widgetId: string;
    @Input() isPopup = false;
    @Input() communityId: number;
    @Input() vendorId: number;
    @Input() workOrderIds: Array<number> = [];
    @Input() workflowId: number;
    @Input() showOnlyLinked = false;

    @Output() onWorkOrderSelection = new EventEmitter<number[]>();
    @Output() onWorkOrderDeselection = new EventEmitter<number[]>();

    hideTitle = false;
    minWidthRatio = 30;
    minHeightRatio = 30;
    minWidth = 800;
    minHeight = 600;
    hideFullScreen = false;

    helpLink: string;
    pageTitle: string;
    loadingVisible: boolean;

    tabItems: Array<TabItem> = [];
    selectedTabItem: TabItem;

    workOrderId: number;

    showWorkOrderEditor = false;
    addWorkOrderButtonOptions: string;

    showManageProposals = false;
    showManageWorkAuthorization = false;

    contextManagerSubscription: Subscription;
    customerId: string;

    constructor(
        protected readonly injector: Injector,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly contextManagerService: ContextManagerService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly titleService: Title
    ) {
        super(injector);
        this.route.queryParamMap.subscribe((qp) => {
            const uuid = qp.get('uuid');
            if (Utils.notNullOrEmpty(uuid)) {
                const filters = localStorage.getItem(uuid);
                if (Utils.notNullOrEmpty(filters)) {
                    const params = JSON.parse(filters);
                    this.communityId = params.communityId;
                    this.workOrderId = Utils.notNullAndDefined(params.workOrderId) ? params.workOrderId : null;
                    this.refreshWorkOrders();
                    localStorage.removeItem(uuid);
                }
                window.history.replaceState({}, document.title, window.location.href.split('?')[0]);
            }
        });

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const routeData = this.route.snapshot.data;
                if (Utils.notNullOrEmpty(routeData)) {
                    this.pageTitle = routeData.pageTitle;
                    this.helpLink = this.ciraHelpService.getHelpLink(null, routeData.helpKey, null);
                    this.titleService.setTitle(this.pageTitle);
                }
            }
        });

        this.workOrderHelperService.onLoadingEvent().subscribe((flag) => {
            setTimeout(() => (this.loadingVisible = flag), 10);
        });

        this.onNewWorkOrderClick = this.onNewWorkOrderClick.bind(this);
    }

    ngOnInit(): void {
        this.configureWorkOrders();
        this.updateSelectedContext();
        this.contextManagerSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.updateSelectedContext();
        });

        this.route.queryParams.subscribe((params: Params) => {
            const communityId = Utils.notNullAndDefined(params['communityId']) ? parseInt(params['communityId']) : null;
            const workOrderId = Utils.notNullAndDefined(params['workOrderId']) ? parseInt(params['workOrderId']) : null;
            const vendorId = Utils.notNullAndDefined(params['vendorId']) ? parseInt(params['vendorId']) : null;

            if (Utils.notNullAndDefined(workOrderId)) {
                this.workOrderId = workOrderId;
            }
            if (Utils.notNullAndDefined(vendorId) && Utils.notNullAndDefined(communityId)) {
                this.vendorId = vendorId;
                this.communityId = communityId;
                this.refreshWorkOrders();
            } else if (Utils.notNullAndDefined(communityId) && Utils.isNullOrUndefined(vendorId)) {
                this.communityId = communityId;
                this.refreshWorkOrders();
            } else if (Utils.notNullAndDefined(vendorId) && Utils.isNullOrUndefined(communityId)) {
                this.vendorId = vendorId;
                this.refreshWorkOrders();
            }
        });
    }

    refresh(width: number, height: number): void {
        // Do Nothing
    }

    getTitle(name: string): string {
        this.hideTitle = true;
        return name;
    }

    ngOnDestroy(): void {
        if (Utils.notNullAndDefined(this.contextManagerSubscription)) {
            this.contextManagerSubscription.unsubscribe();
        }
    }

    onNewWorkOrderClick(): void {
        this.resetWorkOrder();
        this.showWorkOrderEditor = true;
    }

    onWorkOrderEditorShow(): void {
        this.showWorkOrderEditor = true;
    }

    onEditorClose(forceRefresh: boolean): void {
        this.showWorkOrderEditor = false;

        if (Utils.isTrue(forceRefresh)) {
            this.refreshWorkOrders();
        }
    }

    onManageProposalShow(workOrderInfo: WorkOrderRawDto): void {
        if (Utils.notNullAndDefined(workOrderInfo)) {
            this.communityId = workOrderInfo.communityId;
            this.workOrderId = workOrderInfo.workOrderId;
        }

        this.showManageProposals = true;
    }

    onManageProposalClose(forceReload: boolean): void {
        if (Utils.isTrue(forceReload)) {
            this.refreshWorkOrders();
        }

        this.showManageProposals = false;
    }

    onManageWorkAuthorizationShow(workOrderInfo: WorkOrderRawDto): void {
        if (Utils.notNullAndDefined(workOrderInfo)) {
            this.communityId = workOrderInfo.communityId;
            this.workOrderId = workOrderInfo.workOrderId;
        }

        this.showManageWorkAuthorization = true;
    }

    onManageWorkorderAuthorizationClose(): void {
        this.showManageWorkAuthorization = false;
    }

    onWorkOrderRecordsSelection(records: Array<number>): void {
        this.onWorkOrderSelection.emit(records);
    }

    onWorkOrderRecordsDeselection(records: Array<number>): void {
        this.onWorkOrderDeselection.emit(records);
    }

    private resetWorkOrder(): void {
        this.communityId = null;
        this.workOrderId = null;
        this.customerId = null;
        this.updateSelectedContext();
    }

    private configureWorkOrders(): void {
        this.isInternalUser = this.userService.isInternal();
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);
        this.dateFormat = StringFormatConstants.dateFormat;

        this.tabItems = [
            {
                text: 'Open Work Orders',
                name: 'openWorkOrders'
            },
            {
                text: 'Closed Work Orders',
                name: 'closedWorkOrders'
            }
        ];
        this.selectedTabItem = this.tabItems[0];

        this.addWorkOrderButtonOptions = JSON.stringify({
            beforeIcons: [this.iconPlus],
            label: 'Add New Work Order'
        });
    }

    private updateSelectedContext(): void {
        const selectedContext = this.contextManagerService.getSelectedContext();
        if (Utils.notNullOrEmpty(selectedContext)) {
            this.communityId = selectedContext.communityId;
            this.customerId = selectedContext.customerId;
        }
    }

    public refreshWorkOrders(): void {
        if (
            Utils.equalsIgnoreCase(this.selectedTabItem.name, 'openWorkOrders') &&
            Utils.notNullOrEmpty(this.openWorkOrders)
        ) {
            this.openWorkOrders.forceRefresh();
        } else if (
            Utils.equalsIgnoreCase(this.selectedTabItem.name, 'closeWorkOrders') &&
            Utils.notNullOrEmpty(this.closedWorkedOrders)
        ) {
            this.closedWorkedOrders.forceRefresh();
        }
    }
}
